import { Billboard } from '@react-three/drei';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { useRef } from 'react';
import * as THREE from 'three';

const HOTSPOT_DOT_RADIUS = 0.12;
const HOTSPOT_RING_INNER_RADIUS = 0.17;
const HOTSPOT_RING_OUTER_RADIUS = 0.19;
const HOTSPOT_HIT_RADIUS = 0.42;

export function MinimalHotspot({
  position,
  active,
  hovered,
  colorDefault = '#9aa4ad',
  colorHover = '#58b0ff',
  colorActive = '#ffb84d',
  onPointerEnter,
  onPointerLeave,
  onClick,
}: {
  position: THREE.Vector3;
  active: boolean;
  hovered: boolean;
  colorDefault?: string;
  colorHover?: string;
  colorActive?: string;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  onClick?: (e: ThreeEvent<MouseEvent>) => void;
}) {
  const group = useRef<THREE.Group>(null!);
  useFrame((_, dt) => {
    const target = hovered ? 1.1 : active ? 1.06 : 1;
    const s = group.current.scale.x;
    const ns = THREE.MathUtils.damp(s, target, 6, dt);
    group.current.scale.setScalar(ns);
  });

  const color = hovered ? colorHover : active ? colorActive : colorDefault;
  const handlePointerEnter = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    onPointerEnter?.();
  };
  const handlePointerLeave = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    onPointerLeave?.();
  };
  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
  };
  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    onClick?.(e);
  };

  return (
    <group ref={group} position={position}>
      <Billboard follow>
        <mesh
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onPointerDown={handlePointerDown}
          onClick={handleClick}>
          <circleGeometry args={[HOTSPOT_HIT_RADIUS, 32]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
        </mesh>

        {/* dot */}
        <mesh onPointerDown={handlePointerDown} onClick={handleClick}>
          <circleGeometry args={[HOTSPOT_DOT_RADIUS, 32]} />
          <meshBasicMaterial color={color} />
        </mesh>

        {/* ring */}
        <mesh onPointerDown={handlePointerDown} onClick={handleClick}>
          <ringGeometry args={[HOTSPOT_RING_INNER_RADIUS, HOTSPOT_RING_OUTER_RADIUS, 48]} />
          <meshBasicMaterial color={color} transparent opacity={0.9} />
        </mesh>
      </Billboard>
    </group>
  );
}
